import { Controller, useFormContext } from 'react-hook-form';
import { Button, Grid, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import {
  DocumentIngestion_ExtractFieldsResult,
  Estate_UpdateEstateInput,
  EstateType,
} from 'queries';

import FormDateField from 'common/form-date-field';
import FormInputErrorMessage from 'common/form-input-error-message';
import FormPhoneField from 'common/form-phone-field';
import FormSelectField from 'common/form-select-field';
import FormSsnField from 'common/form-ssn-field';
import FormTextField from 'common/form-text-field';
import PortalUsersAutocomplete, {
  UserAutoCompleteOption,
} from 'pages/common/portal/portal-users-autocomplete';

import { EstateDetailDocumentIngestionPrompt } from './estate-detail-document-ingestion-prompt';

const useStyles = makeStyles(() =>
  createStyles({
    h5: {
      paddingTop: '5px',
    },
    formSection: {
      paddingBottom: '10px',
    },
    inputDropdown: {
      width: '100%',
      backgroundColor: 'white',
      height: '4.5vh',
    },
    inputGrid: {
      paddingRight: '20px',
    },
    copyBtnContainer: {
      marginTop: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    infoText: {
      opacity: 0.6,
      fontSize: '14px',
    },
  }),
);

type Props = {
  portalId: string;
  isEditable: boolean;
};

const EstateDetailTruplanningForm = ({ portalId, isEditable }: Props) => {
  const classes = useStyles();
  const {
    formState: { errors },
    control,
    watch,
    setValue,
  } = useFormContext<Estate_UpdateEstateInput>();

  const clientAddressStreet = watch('estateTruplanningFields.clientAddressStreet');
  const clientAddressCity = watch('estateTruplanningFields.clientAddressCity');
  const clientAddressState = watch('estateTruplanningFields.clientAddressState');
  const clientAddressZip = watch('estateTruplanningFields.clientAddressZip');
  const isClientMarried = watch('estateTruplanningFields.isClientMarried');

  const isSpouseAddressCopyVisible =
    isClientMarried &&
    (clientAddressStreet || clientAddressCity || clientAddressState || clientAddressZip);

  const handleCopyToSpouseAddress = () => {
    setValue('estateTruplanningFields.spouseAddressStreet', clientAddressStreet);
    setValue('estateTruplanningFields.spouseAddressCity', clientAddressCity);
    setValue('estateTruplanningFields.spouseAddressState', clientAddressState);
    setValue('estateTruplanningFields.spouseAddressZip', clientAddressZip);
  };

  const handleExtractSpouseFields = (extractedFields: DocumentIngestion_ExtractFieldsResult) => {
    setValue('estateTruplanningFields.spouseFirstName', extractedFields.firstName || '');
    setValue('estateTruplanningFields.spouseMiddleName', extractedFields.middleName || '');
    setValue('estateTruplanningFields.spouseLastName', extractedFields.lastName || '');
    setValue('estateTruplanningFields.spouseSsn', extractedFields.ssn || '');
    setValue('estateTruplanningFields.spouseDOB', extractedFields.dateOfBirth || '');
    setValue(
      'estateTruplanningFields.spouseAddressStreet',
      extractedFields.lastAddressStreet || '',
    );
    setValue('estateTruplanningFields.spouseAddressCity', extractedFields.lastAddressCity || '');
    setValue('estateTruplanningFields.spouseAddressState', extractedFields.lastAddressState || '');
    setValue('estateTruplanningFields.spouseAddressZip', extractedFields.lastAddressZip || '');
  };

  return (
    <>
      <div className={classes.formSection}>
        <Typography variant="h5" className={classes.h5}>
          Estate Planning Client or Prospect
        </Typography>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruplanningFields.clientFirstName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="First Name"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruplanningFields?.clientFirstName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruplanningFields.clientMiddleName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Middle Name or Initial"
                  helperText="*Optional. Please leave blank if no middle name."
                  errorMessage={errors.estateTruplanningFields?.clientMiddleName?.message}
                  inputProps={{ maxLength: 80 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.inputGrid}>
            <Controller
              name="estateTruplanningFields.clientLastName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Last Name"
                  errorMessage={errors.estateTruplanningFields?.clientLastName?.message}
                  inputProps={{ maxLength: 80 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruplanningFields.clientEmailAddress"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Email Address"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruplanningFields?.clientEmailAddress?.message}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} sm={6} className={classes.inputGrid}>
            <Controller
              name="estateTruplanningFields.isClientMarried"
              control={control}
              render={({ field: { ref, value, onChange, ...field } }) => (
                <FormSelectField
                  label="Is client married?"
                  {...field}
                  value={value === true ? 'yes' : 'no'}
                  onChange={(evt) => onChange(evt.target.value === 'yes')}
                  inputRef={ref}
                  options={[
                    {
                      label: 'Yes',
                      value: 'yes',
                    },
                    {
                      label: 'No',
                      value: 'no',
                    },
                  ]}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} sm={6} className={classes.inputGrid}>
            <Controller
              name="estateTruplanningFields.clientPhoneNumber"
              control={control}
              render={({ field: { name, onBlur, onChange, value } }) => (
                <FormPhoneField
                  name={name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  errorMessage={errors.estateTruplanningFields?.clientPhoneNumber?.message}
                  label="Phone Number"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruplanningFields.clientSsn"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormSsnField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  label="SSN"
                  errorMessage={errors.estateTruplanningFields?.clientSsn?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} className={classes.inputGrid}>
            <Controller
              name="estateTruplanningFields.clientDateOfBirth"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <FormDateField
                  {...{ value, onChange, name }}
                  label="Date of Birth"
                  errorMessage={
                    (errors.estateTruplanningFields?.clientDateOfBirth?.message as string) ||
                    undefined
                  }
                />
              )}
            />
          </Grid>
          <Grid style={{ display: 'inline' }} item xs={12} sm={12}>
            <Controller
              name="estateTruplanningFields.clientAddressStreet"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  label="Address Street"
                  errorMessage={errors.estateTruplanningFields?.clientAddressStreet?.message}
                />
              )}
            />
          </Grid>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruplanningFields.clientAddressCity"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  label="Address City"
                  errorMessage={errors.estateTruplanningFields?.clientAddressCity?.message}
                />
              )}
            />
          </Grid>
          <Grid className={classes.inputGrid} item xs={3} sm={2}>
            <Controller
              name="estateTruplanningFields.clientAddressState"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  label="State"
                  errorMessage={errors.estateTruplanningFields?.clientAddressState?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={9} sm={4}>
            <Controller
              name="estateTruplanningFields.clientAddressZip"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  inputProps={{ maxLength: 10 }}
                  disabled={!isEditable}
                  label="Address Zip"
                  errorMessage={errors.estateTruplanningFields?.clientAddressZip?.message}
                />
              )}
            />
          </Grid>
        </Grid>
        {isSpouseAddressCopyVisible && (
          <div className={classes.copyBtnContainer}>
            <Button variant="contained" type="button" onClick={handleCopyToSpouseAddress}>
              Copy to Spouse Address
            </Button>
            <Typography className={classes.infoText}>
              Click the copy button to duplicate the above address to the spouse's address field.
            </Typography>
          </div>
        )}
      </div>
      {isClientMarried && (
        <div className={classes.formSection}>
          <Typography variant="h5" className={classes.h5}>
            Spouse Information
          </Typography>
          <EstateDetailDocumentIngestionPrompt
            portalId={portalId}
            onExtractFields={handleExtractSpouseFields}
            estateType={EstateType.Truplanning}
          />
          <Grid container>
            <Grid item xs={12} sm={6} className={classes.inputGrid}>
              <Controller
                name="estateTruplanningFields.spouseFirstName"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    inputProps={{ maxLength: 80 }}
                    disabled={!isEditable}
                    label="First Name"
                    errorMessage={errors.estateTruplanningFields?.spouseFirstName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="estateTruplanningFields.spouseMiddleName"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    inputProps={{ maxLength: 80 }}
                    disabled={!isEditable}
                    label="Middle Name or Initial"
                    helperText="*Optional. Please leave blank if no middle name."
                    errorMessage={errors.estateTruplanningFields?.spouseMiddleName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} className={classes.inputGrid}>
              <Controller
                name="estateTruplanningFields.spouseLastName"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    inputProps={{ maxLength: 80 }}
                    disabled={!isEditable}
                    label="Last Name"
                    errorMessage={errors.estateTruplanningFields?.spouseLastName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="estateTruplanningFields.spouseSsn"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSsnField
                    {...field}
                    inputRef={ref}
                    inputProps={{ maxLength: 80 }}
                    disabled={!isEditable}
                    label="SSN"
                    errorMessage={errors.estateTruplanningFields?.spouseSsn?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} sm={6} className={classes.inputGrid}>
              <Controller
                name="estateTruplanningFields.spouseEmailAddress"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    inputProps={{ maxLength: 80 }}
                    disabled={!isEditable}
                    label="Email Address"
                    errorMessage={errors.estateTruplanningFields?.spouseEmailAddress?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="estateTruplanningFields.spousePhoneNumber"
                control={control}
                render={({ field: { name, onBlur, onChange, value } }) => (
                  <FormPhoneField
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    errorMessage={errors.estateTruplanningFields?.spousePhoneNumber?.message}
                    label="Phone Number"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} className={classes.inputGrid}>
              <Controller
                name="estateTruplanningFields.spouseDOB"
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <FormDateField
                    {...{ value, onChange, name }}
                    label="Date of Birth"
                    errorMessage={
                      (errors.estateTruplanningFields?.spouseDOB?.message as string) || undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid style={{ display: 'inline' }} item xs={12} sm={12}>
              <Controller
                name="estateTruplanningFields.spouseAddressStreet"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    inputProps={{ maxLength: 80 }}
                    disabled={!isEditable}
                    label="Address Street"
                    errorMessage={errors.estateTruplanningFields?.spouseAddressStreet?.message}
                  />
                )}
              />
            </Grid>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Controller
                name="estateTruplanningFields.spouseAddressCity"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    inputProps={{ maxLength: 80 }}
                    disabled={!isEditable}
                    label="Address City"
                    errorMessage={errors.estateTruplanningFields?.spouseAddressCity?.message}
                  />
                )}
              />
            </Grid>
            <Grid className={classes.inputGrid} item xs={3} sm={2}>
              <Controller
                name="estateTruplanningFields.spouseAddressState"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    inputProps={{ maxLength: 80 }}
                    disabled={!isEditable}
                    label="State"
                    errorMessage={errors.estateTruplanningFields?.spouseAddressState?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={9} sm={4}>
              <Controller
                name="estateTruplanningFields.spouseAddressZip"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    inputProps={{ maxLength: 10 }}
                    disabled={!isEditable}
                    label="Address Zip"
                    errorMessage={errors.estateTruplanningFields?.spouseAddressZip?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </div>
      )}
      <div className={classes.formSection}>
        <Typography variant="h5" className={classes.h5}>
          Additional Information
        </Typography>
        <Grid container>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruplanningFields.areClientsGrantors"
              control={control}
              render={({ field: { ref, value, onChange, ...field } }) => (
                <FormSelectField
                  label="Are the client(s) the Grantors of the Trust?"
                  {...field}
                  value={value === true ? 'yes' : 'no'}
                  onChange={(evt) => onChange(evt.target.value === 'yes')}
                  inputRef={ref}
                  options={[
                    {
                      label: 'Yes',
                      value: 'yes',
                    },
                    {
                      label: 'No',
                      value: 'no',
                    },
                  ]}
                />
              )}
            />
          </Grid>
        </Grid>
      </div>
      <div className={classes.formSection}>
        <Typography variant="h5" className={classes.h5}>
          Add a Point Person
        </Typography>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={12}>
            <Controller
              name="assignedUserId"
              control={control}
              render={({ field: { onChange, value } }) => (
                <PortalUsersAutocomplete
                  elementId="assignedUserId"
                  portalId={portalId}
                  selectedUserId={value || ''}
                  handleUserChange={(
                    _elementId: string,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    _e: any,
                    option: UserAutoCompleteOption | null,
                  ) => onChange(option?.id)}
                />
              )}
            />
            {errors.assignedUserId?.message && (
              <FormInputErrorMessage error={errors.assignedUserId?.message} />
            )}
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default EstateDetailTruplanningForm;
